import React from 'react';
import { ArrowRight, Play, Shield, Activity, CheckCircle } from 'lucide-react';
import { Button } from './ui/button';

const Hero = ({ onDemoClick, onChatOpen }) => {
  return (
    <section className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background */}
      <div className="absolute inset-0 bg-[var(--gradient-hero)]" />
      
      {/* Subtle Wave Pattern */}
      <div className="absolute inset-0 opacity-[0.03]">
        <svg className="w-full h-full" viewBox="0 0 1440 800" preserveAspectRatio="none">
          <path
            d="M0,200 C480,100 960,300 1440,200 L1440,800 L0,800 Z"
            fill="currentColor"
            className="text-[var(--niko-teal)]"
          />
        </svg>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-16 items-center">
          {/* Content */}
          <div className="text-center lg:text-left">
            {/* Badge */}
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--accent-wash)] border border-[var(--niko-teal)]/20 mb-8">
              <Activity className="w-4 h-4 text-[var(--niko-teal)]" />
              <span className="text-sm font-medium text-[var(--niko-teal-dark)]">
                Built by Healthcare Leaders, For Healthcare Teams
              </span>
            </div>

            {/* Headline */}
            <h1 className="heading-1 mb-6">
              The Operating System for{' '}
              <span className="relative inline-block">
                <span className="relative z-10 bg-gradient-to-r from-[var(--niko-teal)] to-[var(--niko-green)] bg-clip-text text-transparent">
                  Safer Care
                </span>
              </span>
            </h1>

            {/* Subheadline */}
            <p className="body-large max-w-xl mx-auto lg:mx-0 mb-8">
              Connect infection prevention, patient safety, quality compliance, and clinical operations 
              in one intelligent platform. From frontline questions to executive dashboards.
            </p>

            {/* Key Benefits */}
            <div className="flex flex-wrap justify-center lg:justify-start gap-4 mb-10">
              {[
                'Evidence-Based Answers',
                'Real-Time Safety Data',
                'Survey-Ready Always'
              ].map((benefit) => (
                <div key={benefit} className="flex items-center gap-2 text-sm text-[var(--text-secondary)]">
                  <CheckCircle className="w-4 h-4 text-[var(--niko-green)]" />
                  {benefit}
                </div>
              ))}
            </div>

            {/* CTAs */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center lg:justify-start">
              <Button
                onClick={onDemoClick}
                className="btn-primary text-base px-8"
              >
                Request a Demo
                <ArrowRight className="w-5 h-5" />
              </Button>
              <Button
                variant="outline"
                onClick={onChatOpen}
                className="btn-secondary text-base px-8"
              >
                <Play className="w-5 h-5" />
                Ask Niko
              </Button>
            </div>
          </div>

          {/* Hero Visual - Clinical Intelligence Engine Preview */}
          <div className="relative lg:pl-8">
            <div className="relative">
              {/* Glow Effect */}
              <div className="absolute -inset-4 bg-gradient-to-r from-[var(--niko-teal)]/20 to-[var(--niko-green)]/20 rounded-3xl blur-3xl" />
              
              {/* Chat Preview Card */}
              <div className="relative bg-white rounded-2xl shadow-2xl border border-[var(--border-light)] overflow-hidden">
                {/* Header */}
                <div className="flex items-center gap-3 px-6 py-4 border-b border-[var(--border-light)] bg-gradient-to-r from-[var(--niko-teal-dark)] to-[var(--niko-teal)]">
                  <div className="relative">
                    <div className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center">
                      <Shield className="w-5 h-5 text-white" />
                    </div>
                    <span className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-[var(--niko-green)] rounded-full border-2 border-white animate-pulse" />
                  </div>
                  <div>
                    <h3 className="font-semibold text-white">Ask Niko</h3>
                    <p className="text-xs text-white/70">PhD-Level Clinical Intelligence</p>
                  </div>
                </div>

                {/* Chat Messages */}
                <div className="p-6 space-y-4 bg-gray-50/50">
                  {/* AI Message */}
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-[var(--border-light)] max-w-sm">
                      <p className="text-sm text-[var(--text-body)]">
                        I'm <strong>Ask Niko</strong> — your PhD-level Clinical Intelligence Engine with CIC, CPHQ, CPPS & CPHRM certifications. How can I help you today?
                      </p>
                    </div>
                  </div>

                  {/* User Message */}
                  <div className="flex gap-3 justify-end">
                    <div className="bg-[var(--niko-teal)] text-white rounded-2xl rounded-tr-sm px-4 py-3 max-w-sm">
                      <p className="text-sm">
                        What are the CLABSI prevention bundle elements?
                      </p>
                    </div>
                  </div>

                  {/* AI Response Preview */}
                  <div className="flex gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-[var(--niko-teal)] to-[var(--niko-green)] flex items-center justify-center flex-shrink-0">
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                    <div className="bg-white rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm border border-[var(--border-light)] max-w-sm">
                      <p className="text-sm text-[var(--text-body)] mb-2">
                        <strong>CLABSI Prevention Bundle Elements:</strong>
                      </p>
                      <ul className="text-sm text-[var(--text-secondary)] space-y-1">
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-[var(--niko-green)] mt-0.5 flex-shrink-0" />
                          Hand hygiene compliance
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-[var(--niko-green)] mt-0.5 flex-shrink-0" />
                          Maximal barrier precautions
                        </li>
                        <li className="flex items-start gap-2">
                          <CheckCircle className="w-4 h-4 text-[var(--niko-green)] mt-0.5 flex-shrink-0" />
                          Chlorhexidine skin antisepsis
                        </li>
                      </ul>
                      <p className="text-xs text-[var(--niko-teal)] mt-2 font-medium">
                        📚 Source: CDC Guidelines, Joint Commission NPSG
                      </p>
                    </div>
                  </div>
                </div>

                {/* Input Preview */}
                <div className="px-6 py-4 border-t border-[var(--border-light)] bg-white">
                  <div className="flex items-center gap-3 px-4 py-3 bg-gray-50 rounded-full border border-[var(--border-light)]">
                    <span className="text-sm text-[var(--text-muted)]">Ask Niko anything about clinical safety...</span>
                  </div>
                </div>
              </div>

              {/* Floating Stats */}
              <div className="absolute -left-4 top-1/4 hidden lg:block">
                <div className="bg-white rounded-xl shadow-lg border border-[var(--border-light)] px-4 py-3 animate-float">
                  <p className="text-2xl font-bold text-[var(--niko-teal)]">PhD+</p>
                  <p className="text-xs text-[var(--text-muted)]">Level Expertise</p>
                </div>
              </div>

              <div className="absolute -right-4 bottom-1/4 hidden lg:block">
                <div className="bg-white rounded-xl shadow-lg border border-[var(--border-light)] px-4 py-3 animate-float" style={{ animationDelay: '1s' }}>
                  <p className="text-2xl font-bold text-[var(--niko-green)]">24/7</p>
                  <p className="text-xs text-[var(--text-muted)]">Always Available</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
